import React, { useEffect, useState } from 'react';
import { X, Loader2, ExternalLink } from 'lucide-react';
import { getDocumentUrl } from '../lib/api';
import { useT } from '../lib/LanguageContext';

/**
 * Inline PDF viewer modal.
 * Fetches a short-lived CloudFront signed URL for the raw S3 object, then
 * renders the PDF in an <iframe> using the browser's native viewer.
 * (No extra deps — browsers ship a PDF viewer in 2026.)
 */
export default function PdfViewer({ docId, title, onClose, fetchUrl }) {
  const t = useT();
  const [url, setUrl] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!docId) return;
    let cancelled = false;
    (async () => {
      try {
        const d = await (fetchUrl ? fetchUrl() : getDocumentUrl(docId));
        if (!cancelled) setUrl(d.url);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message || t('viewer.pdfFailed'));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 flex flex-col"
      data-testid="pdf-viewer-backdrop"
      onClick={onClose}
    >
      <div
        className="flex items-center gap-2 px-3 h-12 bg-keep-surface border-b border-keep-border"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="p-1.5 rounded-full hover:bg-keep-hover"
          aria-label={t('common.close')}
          data-testid="pdf-viewer-close"
        >
          <X className="w-5 h-5 text-keep-textMuted" />
        </button>
        <span className="text-[14px] font-medium text-keep-text truncate flex-1">
          {title || t('source.pdf')}
        </span>
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="p-1.5 rounded-full hover:bg-keep-hover"
            title={t('viewer.openInNewTab')}
            data-testid="pdf-viewer-newtab"
          >
            <ExternalLink className="w-4 h-4 text-keep-textMuted" />
          </a>
        )}
      </div>
      <div
        className="flex-1 bg-keep-page overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {error ? (
          <div className="h-full flex items-center justify-center p-6">
            <p className="text-keep-danger text-[14px]" data-testid="pdf-viewer-error">{error}</p>
          </div>
        ) : !url ? (
          <div className="h-full flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-keep-accent animate-spin" />
          </div>
        ) : (
          <iframe
            src={url}
            title={title || t('source.pdf')}
            className="w-full h-full border-0 bg-white"
            data-testid="pdf-viewer-frame"
          />
        )}
      </div>
    </div>
  );
}
